import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import { SplashScreen } from "./components/SplashScreen";
import { ScrollToTop } from "./components/ScrollToTop";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import NewTicket from "./pages/NewTicket";
import Condomini from "./pages/Condomini";
import Fornitori from "./pages/Fornitori";
import Residenti from "./pages/Residenti";
import Sinistri from "./pages/Sinistri";
import CondominoSinistri from "./pages/CondominoSinistri";
import Reports from "./pages/Reports";
import Scadenze from "./pages/Scadenze";
import Statistiche from "./pages/Statistiche";
import Preventivi from "./pages/Preventivi";
import PreventivoDetail from "./pages/PreventivoDetail";
import Proprieta from "./pages/Proprieta";
import Activate from "./pages/Activate";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Presentazione from "./pages/Presentazione";
import Piani from "./pages/Piani";
import Bacheca from "./pages/Bacheca";
import ControlPanel from "./pages/ControlPanel";
import Impostazioni from "./pages/Impostazioni";
import BrandingStudio from "./pages/BrandingStudio";
import OnboardingWizard from "./components/OnboardingWizard";
import ComunicazioniFornitori from "./pages/ComunicazioniFornitori";
import DocumentiFornitore from "./pages/DocumentiFornitore";
import DocumentiCondominio from "./pages/DocumentiCondominio";
import Guida from "./pages/Guida";
import IncaricoPublic from "./pages/IncaricoPublic";
import CrediusIncaricoPublic from "./pages/CrediusIncaricoPublic";
import PreventivoPublic from "./pages/PreventivoPublic";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentReturn";
import RaccoltaPublic from "./pages/RaccoltaPublic";
import SchedaProfessionistaPublic from "./pages/SchedaProfessionistaPublic";
import MieRaccolte from "./pages/MieRaccolte";
import RacProprietaPublic from "./pages/RacProprietaPublic";
import RaccoltaDati from "./pages/RaccoltaDati";
import Privacy from "./pages/Privacy";
import RichiediProva from "./pages/RichiediProva";
import Ras from "./pages/Ras";
import Appalti from "./pages/Appalti";
import CoPilotSopralluogo from "./pages/CoPilotSopralluogo";
import CoPilotMappa from "./pages/CoPilotMappa";
import CoPilotStatistiche from "./pages/CoPilotStatistiche";
import CoPilotProfilo from "./pages/CoPilotProfilo";
import RasUpload from "./pages/RasUpload";
import ConfermaPreventivo from "./pages/ConfermaPreventivo";
import Credius from "./pages/Credius";
import CrediusNuova from "./pages/CrediusNuova";
import CrediusDetail from "./pages/CrediusDetail";
import MieConvenzioni from "./pages/MieConvenzioni";
import CrediusAnagrafica from "./pages/CrediusAnagrafica";
import CrediusContabilitaAvvocato from "./pages/CrediusContabilitaAvvocato";
import SuperAdmin from "./pages/SuperAdmin";
import ImportHub from "./pages/ImportHub";
import { Loader2 } from "lucide-react";

function Protected({ children, roles, studioOnly, trialSection, allowSuperAdmin, planFeature }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-blue-700" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  // Iter122av · super_admin può accedere al /pannello per gestire onboarding studi (iframe da SuperAdmin)
  if (user.role === "super_admin" && !allowSuperAdmin) return <Navigate to="/superadmin" replace />;
  if (roles && !roles.includes(user.role)) {
    // Iter122l · Step 3 · credius_client viene sempre reindirizzato alla sua unica sezione utile.
    if (user.role === "credius_client") return <Navigate to="/credius" replace />;
    // Iter122k91 · avvocato convenzionato: fallback su Credius quando accede a sezioni non consentite
    if (user.role === "fornitore" && user.is_avvocato) return <Navigate to="/credius" replace />;
    return <Navigate to="/segnalazioni" replace />;
  }
  if (studioOnly && user.account_type === "trial") {
    // Iter122ed · Trial gate legacy `trial_sezioni` disattivato quando plan_features è presente
    const hasPlanFeatures = Array.isArray(user.plan_features) && user.plan_features.length > 0;
    if (!hasPlanFeatures) {
      const enabled = trialSection && (user.trial_sezioni || []).includes(trialSection);
      if (!enabled) return <Navigate to="/" replace />;
    }
  }
  // Iter122eb · Gating per piano condominio (matrice "Piani e funzioni GiniusAPP")
  // Skip per super_admin, admin Izzo interno (tenant_id="studio") e voci senza planFeature.
  if (planFeature && user.role !== "super_admin" && user.tenant_id !== "studio") {
    const feats = user.plan_features || [];
    if (feats.length > 0 && !feats.includes(planFeature)) {
      return <Navigate to={`/piani?upgrade=${encodeURIComponent(planFeature)}`} replace />;
    }
  }
  return <Layout>{children}</Layout>;
}

function SuperProtected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-blue-700" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  // Iter122cd · SOLO super_admin. Admin Studio Izzo NON accede più a /superadmin (né alle tab Trial/Onboarding).
  if (user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user?.role === "admin") return <Dashboard />;
  // Iter122l · Step 3 · credius_client atterra sempre direttamente su Credius.
  if (user?.role === "credius_client") return <Navigate to="/credius" replace />;
  // Iter122k91 · Avvocato convenzionato (fornitore + is_avvocato) atterra su Credius
  //   invece che su Manutenzioni (che è per i manutentori Ginius, non per legali).
  //   Layout.js già nasconde "Manutenzioni" dalla sidebar per gli avvocati.
  if (user?.role === "fornitore" && user?.is_avvocato) return <Navigate to="/credius" replace />;
  return <Navigate to="/segnalazioni" replace />;
}

function ConditionalSplash() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/incarico/") || pathname.startsWith("/preventivo/") || pathname.startsWith("/raccolta/") || pathname.startsWith("/rac/") || pathname.startsWith("/richiedi-prova") || pathname.startsWith("/ras-upload/") || pathname.startsWith("/scheda-professionista/")) return null;
  return <SplashScreen />;
}

/** Iter123k247 · Overlay bloccante wizard di benvenuto per i trial admin.
 *  Si monta a livello App e intercetta ogni pagina autenticata finché i 3 step
 *  non sono completati. Si nasconde su route pubbliche/token/login per non
 *  interferire con flussi pre-autenticazione. */
function OnboardingGate() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Non mostrare mai su route pubbliche o pre-login.
  const publicPrefixes = ["/login", "/presentazione", "/piani", "/attiva",
    "/password-dimenticata", "/reset-password", "/incarico/",
    "/credius/incarico/", "/preventivo/", "/payment/", "/raccolta/", "/rac/",
    "/richiedi-prova", "/ras-upload/", "/scheda-professionista/"];
  if (publicPrefixes.some((p) => pathname === p || pathname.startsWith(p))) return null;
  if (!user) return null;
  // Solo admin trial: gli altri ruoli (super_admin, condomino, fornitore, …) non
  // vedono il wizard anche se account_type===trial per qualche inconsistenza.
  if (user.role !== "admin" || user.account_type !== "trial") return null;
  return <OnboardingWizard />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ConditionalSplash />
          <OnboardingGate />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/presentazione" element={<Presentazione />} />
            <Route path="/piani" element={<Piani />} />
            <Route path="/attiva" element={<Activate />} />
            <Route path="/password-dimenticata" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/incarico/:token" element={<IncaricoPublic />} />
            <Route path="/credius/incarico/:token" element={<CrediusIncaricoPublic />} />
            <Route path="/preventivo/:token" element={<PreventivoPublic />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/raccolta/:token" element={<RaccoltaPublic />} />
            <Route path="/scheda-professionista/:token" element={<SchedaProfessionistaPublic />} />
            <Route path="/rac/:token" element={<RacProprietaPublic />} />
            <Route path="/richiedi-prova" element={<RichiediProva />} />
            <Route path="/ras-upload/:token" element={<RasUpload />} />
            <Route path="/superadmin" element={<SuperProtected><SuperAdmin /></SuperProtected>} />
            <Route path="/" element={<Protected><Home /></Protected>} />
            <Route path="/segnalazioni" element={<Protected><Tickets /></Protected>} />
            <Route path="/segnalazioni/:id" element={<Protected><TicketDetail /></Protected>} />
            <Route path="/nuova" element={<Protected roles={["admin", "condomino"]}><NewTicket /></Protected>} />
            <Route path="/condomini" element={<Protected roles={["admin"]} planFeature="stabili"><Condomini /></Protected>} />
            <Route path="/residenti" element={<Protected roles={["admin"]} planFeature="account_condomini"><Residenti /></Protected>} />
            <Route path="/bacheca" element={<Protected roles={["admin", "condomino"]} planFeature="bacheca"><Bacheca /></Protected>} />
            <Route path="/fornitori" element={<Protected roles={["admin"]} planFeature="account_fornitori"><Fornitori /></Protected>} />
            <Route path="/comunicazioni-fornitori" element={<Protected roles={["admin", "fornitore"]} planFeature="comunicazioni_fornitori"><ComunicazioniFornitori /></Protected>} />
            <Route path="/documenti-richiesti" element={<Protected roles={["fornitore"]}><DocumentiFornitore /></Protected>} />
            <Route path="/documenti" element={<Protected roles={["condomino"]}><DocumentiCondominio /></Protected>} />
            <Route path="/mie-raccolte" element={<Protected roles={["condomino"]}><MieRaccolte /></Protected>} />
            <Route path="/sinistri" element={<Protected roles={["admin", "fornitore"]} studioOnly trialSection="sinistri" planFeature="sinistri"><Sinistri /></Protected>} />
            <Route path="/miei-sinistri" element={<Protected roles={["condomino"]}><CondominoSinistri /></Protected>} />
            <Route path="/scadenze" element={<Protected roles={["admin"]} planFeature="calendario"><Scadenze /></Protected>} />
            <Route path="/statistiche" element={<Protected roles={["admin"]} studioOnly trialSection="statistiche" planFeature="statistiche"><Statistiche /></Protected>} />
            <Route path="/preventivi" element={<Protected roles={["admin", "fornitore"]} studioOnly trialSection="preventivi" planFeature="preventivi"><Preventivi /></Protected>} />
            <Route path="/preventivi/:id" element={<Protected roles={["admin", "fornitore"]} studioOnly trialSection="preventivi" planFeature="preventivi"><PreventivoDetail /></Protected>} />
            <Route path="/conferma-preventivo" element={<Protected roles={["admin"]} studioOnly trialSection="preventivi" planFeature="preventivi"><ConfermaPreventivo /></Protected>} />
            <Route path="/conferma-preventivo/:id" element={<Protected roles={["admin"]} studioOnly trialSection="preventivi" planFeature="preventivi"><ConfermaPreventivo /></Protected>} />
            <Route path="/credius" element={<Protected roles={["admin", "fornitore", "condomino", "credius_client"]} planFeature="credius"><Credius /></Protected>} />
            <Route path="/credius/anagrafica" element={<Protected roles={["admin"]} studioOnly planFeature="credius_anagrafica"><CrediusAnagrafica /></Protected>} />
            <Route path="/credius/nuova" element={<Protected roles={["admin"]} planFeature="credius"><CrediusNuova /></Protected>} />
            {/* Iter122k122 · Contabilità Avvocato — pro-forma pagate + fatture elettroniche */}
            <Route path="/credius/contabilita-avvocato" element={<Protected roles={["fornitore"]} planFeature="credius"><CrediusContabilitaAvvocato /></Protected>} />
            <Route path="/credius/:id" element={<Protected roles={["admin", "fornitore", "condomino", "credius_client"]} planFeature="credius"><CrediusDetail /></Protected>} />
            {/* Iter122k72 · Sezione fornitore convenzionato */}
            <Route path="/mie-convenzioni" element={<Protected roles={["fornitore"]}><MieConvenzioni /></Protected>} />
            <Route path="/proprieta" element={<Protected roles={["admin", "condomino"]} planFeature="anagrafica"><Proprieta /></Protected>} />
            <Route path="/report" element={<Protected roles={["admin"]} studioOnly trialSection="report" planFeature="report"><Reports /></Protected>} />
            <Route path="/pannello" element={<Protected roles={["admin", "super_admin"]} studioOnly allowSuperAdmin planFeature="impostazioni_account"><ControlPanel /></Protected>} />
            <Route path="/branding-studio" element={<Protected roles={["admin"]}><BrandingStudio /></Protected>} />
            <Route path="/impostazioni" element={<Protected roles={["admin"]}><Impostazioni /></Protected>} />
            <Route path="/import" element={<Protected roles={["admin"]} studioOnly planFeature="importa_dati"><ImportHub /></Protected>} />
            <Route path="/raccolta-dati" element={<Protected roles={["admin"]} studioOnly trialSection="raccolta_dati" planFeature="raccolta_dati"><RaccoltaDati /></Protected>} />
            <Route path="/ras" element={<Protected roles={["admin"]} studioOnly trialSection="ras" planFeature="ras"><Ras /></Protected>} />
            <Route path="/appalti" element={<Protected roles={["admin", "fornitore", "condomino"]} planFeature="appalti"><Appalti /></Protected>} />
            <Route path="/copilot/mappa" element={<Protected roles={["admin"]} planFeature="appalti"><CoPilotMappa /></Protected>} />
            <Route path="/copilot/statistiche" element={<Protected roles={["admin"]} planFeature="appalti"><CoPilotStatistiche /></Protected>} />
            <Route path="/copilot/profilo" element={<Protected roles={["admin"]} planFeature="appalti"><CoPilotProfilo /></Protected>} />
            <Route path="/copilot/:sid" element={<Protected roles={["admin"]} planFeature="appalti"><CoPilotSopralluogo /></Protected>} />
            <Route path="/privacy" element={<Protected roles={["admin"]} studioOnly planFeature="privacy"><Privacy /></Protected>} />
            <Route path="/guida" element={<Protected allowSuperAdmin><Guida /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
